import { useEffect, useRef, useState, type FormEvent, type ReactNode } from 'react';
import { Link, useParams } from 'react-router-dom';
import * as Dialog from '@radix-ui/react-dialog';
import {
  ArrowRight,
  Building2,
  CheckCircle2,
  CircleDashed,
  FileText,
  Flag,
  Layers3,
  ListChecks,
  Loader2,
  Sparkles,
  Target,
  Users,
  UserPlus,
  X,
} from 'lucide-react';
import {
  companiesApi,
  organizationalApi,
  objectivesApi,
  tasksApi,
  projectsApi,
  type Company,
  type TeamMember,
  type Task,
  type Project,
} from '../services/api';
import {
  DASHBOARD_STAGES,
  getNextStage,
  getPlanningProgress,
  type DashboardProgress,
} from '../utils/dashboard';
import { loadCompanyProgress } from '../utils/companyProgress';
import './DashboardPage.css';
import './CompanyDashboardPage.css';
import '../ai-plans/Plans.css';

type Objective = NonNullable<
  Awaited<ReturnType<typeof objectivesApi.getObjectives>>['data']
>[number];
type OrganizationSummary = NonNullable<
  Awaited<ReturnType<typeof organizationalApi.getOrganizationalMetrics>>['data']
>['summary'];
interface OverviewData {
  company: Company;
  progress: DashboardProgress | null;
  members: TeamMember[] | null;
  objectives: Objective[] | null;
  tasks: Task[] | null;
  projects: Project[] | null;
  organization: OrganizationSummary | null;
  unavailable: string[];
}

function formatTarget(value: string): string {
  const date = new Date(value);
  return Number.isNaN(date.getTime())
    ? value
    : new Intl.DateTimeFormat('en', {
        day: 'numeric',
        month: 'short',
        year: 'numeric',
        timeZone: 'UTC',
      }).format(date);
}

function Section({
  title,
  subtitle,
  icon,
  to,
  action,
  children,
}: {
  title: string;
  subtitle: string;
  icon: ReactNode;
  to: string;
  action: string;
  children: ReactNode;
}) {
  return (
    <section className="company-overview-card">
      <div className="company-overview-card-heading">
        <span className="dashboard-icon blue">{icon}</span>
        <div>
          <h2>{title}</h2>
          <p>{subtitle}</p>
        </div>
        <Link to={to} aria-label={`${action}: ${title}`}>
          {action}
          <ArrowRight size={16} />
        </Link>
      </div>
      {children}
    </section>
  );
}

export default function CompanyDashboardPage() {
  const { companyId } = useParams<{ companyId: string }>();
  const [data, setData] = useState<OverviewData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [reload, setReload] = useState(0);
  const [inviteOpen, setInviteOpen] = useState(false);
  const [email, setEmail] = useState('');
  const [role, setRole] = useState('member');
  const [inviting, setInviting] = useState(false);
  const inviteRequest = useRef(0);
  const [inviteResult, setInviteResult] = useState<{ success: boolean; message: string } | null>(
    null
  );

  useEffect(() => {
    let cancelled = false;
    inviteRequest.current += 1;
    setInviting(false);
    setLoading(true);
    setData(null);
    setError(null);
    setInviteOpen(false);
    setInviteResult(null);
    setEmail('');
    setRole('member');
    if (!companyId) {
      setLoading(false);
      setError('Choose a company from your dashboard to continue.');
      return;
    }
    async function load() {
      const results = await Promise.allSettled([
        companiesApi.get(companyId!),
        loadCompanyProgress(companyId!),
        companiesApi.getTeamMembers(companyId!),
        objectivesApi.getObjectives(companyId!),
        tasksApi.list({ company: companyId! }),
        projectsApi.getAllProjects(companyId!),
        organizationalApi.getOrganizationalMetrics(companyId!),
      ] as const);
      if (cancelled) return;
      const companyResult = results[0];
      if (
        companyResult.status !== 'fulfilled' ||
        !companyResult.value.success ||
        !companyResult.value.data
      ) {
        setError('We couldn’t load this company. Try again, or return to your dashboard.');
        setLoading(false);
        return;
      }
      const unavailable: string[] = [];
      function read<T>(
        result: PromiseSettledResult<{ success: boolean; data?: T; error?: unknown }>,
        name: string
      ): T | null {
        if (
          result.status === 'fulfilled' &&
          result.value.success &&
          result.value.data !== undefined
        )
          return result.value.data;
        unavailable.push(name);
        return null;
      }
      const planning = results[1].status === 'fulfilled' ? results[1].value : null;
      unavailable.push(...(planning?.unavailable ?? ['planning progress']));
      const progress = planning?.progress ?? null;
      const members = read(results[2], 'people');
      const objectives = read(results[3], 'objectives');
      const taskData = read(results[4], 'tasks');
      const projects = read(results[5], 'projects');
      const organization = read(results[6], 'organization');
      setData({
        company: companyResult.value.data,
        progress,
        members,
        objectives,
        tasks: taskData?.all ?? null,
        projects,
        organization: organization?.summary ?? null,
        unavailable,
      });
      setLoading(false);
    }
    void load();
    return () => {
      cancelled = true;
      inviteRequest.current += 1;
    };
  }, [companyId, reload]);

  async function invite(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    if (!companyId || !email.trim() || inviting) return;
    const requestId = ++inviteRequest.current;
    setInviting(true);
    setInviteResult(null);
    try {
      const response = await companiesApi.inviteUser(companyId, { email: email.trim(), role });
      if (requestId !== inviteRequest.current) return;
      if (!response.success || !response.data)
        throw new Error(response.error || 'Please try again.');
      setInviteResult({
        success: true,
        message:
          response.data.status === 'added'
            ? `${response.data.email} has been added to your company.`
            : `Invitation sent to ${response.data.email}.`,
      });
      setEmail('');
      // Refresh failure must not turn a successful invitation into an error.
      try {
        const members = await companiesApi.getTeamMembers(companyId);
        if (members.success && members.data)
          setData((previous) =>
            previous?.company.id === companyId ? { ...previous, members: members.data! } : previous
          );
      } catch {
        // The invitation result is authoritative; membership refresh can be retried later.
      }
    } catch (err) {
      if (requestId !== inviteRequest.current) return;
      setInviteResult({
        success: false,
        message:
          err instanceof Error
            ? err.message
            : 'We couldn’t send this invitation. Please try again.',
      });
    } finally {
      if (requestId === inviteRequest.current) setInviting(false);
    }
  }

  if (loading)
    return (
      <div className="company-overview-state" role="status">
        <Loader2 className="animate-spin" size={25} />
        <h1>Opening your company workspace…</h1>
        <p>Bringing your plan, people and work together.</p>
      </div>
    );
  if (error || !data)
    return (
      <div className="company-overview-state">
        <Building2 size={32} />
        <h1>Company unavailable</h1>
        <p>{error}</p>
        <div className="company-overview-actions">
          <button
            type="button"
            className="dashboard-button dashboard-button-primary"
            onClick={() => setReload((value) => value + 1)}
          >
            Try again
          </button>
          <Link to="/dashboard" className="dashboard-button dashboard-button-secondary">
            Back to dashboard
          </Link>
        </div>
      </div>
    );

  const { company, progress, members, objectives, tasks, projects, organization, unavailable } =
    data;
  const canManage =
    company.capabilities?.manageMembers ?? (company.role === 'owner' || company.role === 'admin');
  const planning = progress ? getPlanningProgress(progress) : null;
  const next = progress ? getNextStage(progress) : null;
  const openTasks = tasks?.filter((task) => task.status !== 'completed') ?? null;
  const activeProjects =
    projects?.filter((project) => project.status === 'active' || project.status === 'planning') ??
    null;
  const route = (path: string) => `/company/${companyId}/${path}`;
  const count = (value: number | null | undefined) => value ?? '—';
  const nextTitle =
    next?.key === 'executionProgress'
      ? 'Put your plan into motion.'
      : next
        ? `Your next step: ${next.title.toLowerCase()}.`
        : 'Build a clear path from vision to action.';
  const metrics = [
    {
      label: 'Planning progress',
      value: planning === null ? '—' : `${planning}%`,
      detail: 'Across the four planning stages',
      icon: <Target size={21} />,
      color: 'blue',
    },
    {
      label: 'Strategic objectives',
      value: count(objectives?.length),
      detail: 'The outcomes you’re working toward',
      icon: <Flag size={21} />,
      color: 'purple',
    },
    {
      label: 'Open tasks',
      value: count(openTasks?.length),
      detail: tasks
        ? `${tasks.filter((task) => task.status === 'completed').length} completed tasks`
        : 'Task data unavailable',
      icon: <ListChecks size={21} />,
      color: 'blue',
    },
    {
      label: 'Company members',
      value: count(members?.length),
      detail: organization
        ? `${organization.totalTeams} teams · ${organization.totalDivisions} divisions`
        : 'People connected to your company',
      icon: <Users size={21} />,
      color: 'purple',
    },
  ];

  return (
    <div className="company-overview">
      <div className="company-overview-intro">
        <div>
          <p className="dashboard-eyebrow">YOUR COMPANY WORKSPACE</p>
          <h1>{company.name}</h1>
          <p>Your direction, your people and your next steps. Connected in one place.</p>
          <div className="company-overview-meta">
            <span>{company.industry}</span>
            <span>{company.size} company</span>
            {company.role && <span>{company.role}</span>}
          </div>
        </div>
        <div className="company-overview-actions">
          <Link to={route('visual-report')} className="dashboard-button dashboard-button-secondary">
            <FileText size={17} /> View your plan
          </Link>
          {canManage && (
            <button
              type="button"
              onClick={() => {
                setInviteResult(null);
                setInviteOpen(true);
              }}
              className="dashboard-button dashboard-button-primary"
            >
              <UserPlus size={17} /> Invite people
            </button>
          )}
        </div>
      </div>
      {unavailable.length > 0 && (
        <div className="company-overview-notice" role="status">
          <span>Some company information couldn’t load: {unavailable.join(', ')}.</span>
          <button type="button" onClick={() => setReload((value) => value + 1)}>
            Retry
          </button>
        </div>
      )}
      <section className="company-overview-next" aria-label="Recommended next step">
        <span className="company-overview-next-icon">
          <Sparkles size={27} />
        </span>
        <div>
          <p className="dashboard-eyebrow">KEEP YOUR STRATEGY MOVING</p>
          <h2>{nextTitle}</h2>
          <p>
            {next?.description ??
              'Follow guided frameworks, develop your priorities and turn them into practical work.'}
          </p>
        </div>
        <Link
          to={route(next?.path ?? 'strategy')}
          className="dashboard-button dashboard-button-primary"
        >
          {next?.key === 'executionProgress' ? 'Open execution' : 'Continue your plan'}
          <ArrowRight size={17} />
        </Link>
      </section>
      <section className="ai-plan-entry" aria-label="AI strategic planning">
        <div>
          <strong>Create a complete plan for this company</strong>
          <p>
            Review the brief, research competitors, and generate your strategy, projects and client
            report.
          </p>
        </div>
        <Link to={route('ai-plan')}>
          Open AI strategic plans <Sparkles size={16} />
        </Link>
      </section>
      <section className="company-overview-metrics" aria-label="Company at a glance">
        {metrics.map((metric) => (
          <div className="company-overview-metric" key={metric.label}>
            <span className={`dashboard-icon ${metric.color}`}>{metric.icon}</span>
            <div>
              <p>{metric.label}</p>
              <strong>{metric.value}</strong>
              <small>{metric.detail}</small>
            </div>
          </div>
        ))}
      </section>
      <section className="company-overview-journey" aria-labelledby="company-journey-heading">
        <div className="company-overview-section-heading">
          <div>
            <p className="dashboard-eyebrow">ONE CONNECTED JOURNEY</p>
            <h2 id="company-journey-heading">From your first idea to real progress.</h2>
          </div>
          <Link to={route('strategy')}>
            Open strategic plan
            <ArrowRight size={16} />
          </Link>
        </div>
        <div className="company-overview-stages">
          {DASHBOARD_STAGES.map((stage, index) => {
            const value = progress
              ? Math.max(
                  0,
                  Math.min(100, Number.isFinite(progress[stage.key]) ? progress[stage.key] : 0)
                )
              : null;
            return (
              <Link
                to={route(stage.path)}
                className={`company-overview-stage ${next?.key === stage.key ? 'is-next' : ''}`}
                key={stage.key}
              >
                <div className="company-overview-stage-top">
                  <span>{index + 1}</span>
                  {value === 100 ? (
                    <CheckCircle2 size={19} aria-label="Complete" />
                  ) : (
                    <ArrowRight size={17} />
                  )}
                </div>
                <h3>{stage.title}</h3>
                <p>{stage.description}</p>
                <div
                  className="company-overview-progress"
                  role="progressbar"
                  aria-label={`${stage.title} progress`}
                  {...(value !== null ? { 'aria-valuenow': value } : {})}
                  aria-valuemin={0}
                  aria-valuemax={100}
                >
                  <span style={{ width: `${value ?? 0}%` }} />
                </div>
                <small>
                  {value === null
                    ? 'Progress unavailable'
                    : value === 100
                      ? 'Complete'
                      : value === 0
                        ? 'Ready to start'
                        : `${value}% complete`}
                </small>
              </Link>
            );
          })}
        </div>
      </section>
      <div className="company-overview-grid">
        <Section
          title="Work in motion"
          subtitle="The tasks and projects bringing your plan to life."
          icon={<ListChecks size={20} />}
          to={route('execution')}
          action="Open execution"
        >
          <div className="company-overview-inline-stats">
            <span>
              <strong>{count(openTasks?.length)}</strong> open tasks
            </span>
            <span>
              <strong>{count(activeProjects?.length)}</strong> active or planned projects
            </span>
          </div>
          {openTasks === null ? (
            <p className="company-overview-empty">
              Task information is unavailable. Try refreshing this workspace.
            </p>
          ) : openTasks.length === 0 ? (
            <div className="company-overview-empty">
              <CircleDashed size={27} />
              <h3>Your next move starts here.</h3>
              <p>
                Break a strategic priority into a project, then add the tasks and people to make it
                happen.
              </p>
              <Link to={route('execution')}>
                Plan your first piece of work
                <ArrowRight size={16} />
              </Link>
            </div>
          ) : (
            <ul className="company-overview-list">
              {openTasks.slice(0, 4).map((task) => (
                <li key={task.id}>
                  <span className={`company-overview-task-dot ${task.priority}`} />
                  <div>
                    <Link to={route('execution')}>{task.title}</Link>
                    <small>
                      {task.projectName || task.strategyName || 'Company task'}
                      {task.assigneeName ? ` · ${task.assigneeName}` : ''}
                    </small>
                  </div>
                  <span className={`company-overview-status ${task.status}`}>
                    {task.status.replace(/_/g, ' ')}
                  </span>
                </li>
              ))}
            </ul>
          )}
          {activeProjects && activeProjects.length > 0 && (
            <div className="company-overview-projects">
              <p className="dashboard-eyebrow">PROJECTS TO MOVE FORWARD</p>
              {activeProjects.slice(0, 2).map((project) => (
                <Link to={route('execution')} key={project.id}>
                  <Layers3 size={17} />
                  <span>{project.title}</span>
                  <ArrowRight size={15} />
                </Link>
              ))}
            </div>
          )}
        </Section>
        <Section
          title="Your strategic objectives"
          subtitle="Keep the outcomes that matter in focus."
          icon={<Target size={20} />}
          to={route('strategy/foundation/objectives')}
          action="View objectives"
        >
          {objectives === null ? (
            <p className="company-overview-empty">Objective information is unavailable.</p>
          ) : objectives.length === 0 ? (
            <div className="company-overview-empty">
              <Target size={27} />
              <h3>Give your ambition a clear target.</h3>
              <p>
                Define what success looks like, how you’ll measure it and when you want to get
                there.
              </p>
              <Link to={route('strategy/foundation/objectives')}>
                Set your first objective
                <ArrowRight size={16} />
              </Link>
            </div>
          ) : (
            <ul className="company-overview-objectives">
              {objectives.slice(0, 3).map((objective) => (
                <li key={objective.id}>
                  <span>{objective.category}</span>
                  <h3>{objective.goal}</h3>
                  <p>
                    {objective.measure}
                    {objective.byWhen ? ` · ${formatTarget(objective.byWhen)}` : ''}
                  </p>
                </li>
              ))}
            </ul>
          )}
        </Section>
        <Section
          title="People & organization"
          subtitle="Connect your priorities to the people behind them."
          icon={<Users size={20} />}
          to={route('organization')}
          action="Open organization"
        >
          <div className="company-overview-inline-stats">
            <span>
              <strong>{count(organization?.totalDivisions)}</strong> divisions
            </span>
            <span>
              <strong>{count(organization?.totalTeams)}</strong> teams
            </span>
          </div>
          {members === null ? (
            <p className="company-overview-empty">Member information is unavailable.</p>
          ) : (
            <ul className="company-overview-members">
              {members.slice(0, 4).map((member) => (
                <li key={member.id}>
                  <span className="company-overview-avatar">
                    {member.photo ? (
                      <img src={member.photo} alt="" />
                    ) : (
                      (member.name || member.email).slice(0, 2).toUpperCase()
                    )}
                  </span>
                  <div>
                    <strong>{member.name || member.email}</strong>
                    <small>
                      {member.status === 'invited' ? 'Invitation pending' : member.role}
                    </small>
                  </div>
                </li>
              ))}
            </ul>
          )}
          <Link to={route('org-chart')} className="company-overview-text-link">
            Explore your org chart
            <ArrowRight size={16} />
          </Link>
        </Section>
        <section className="company-overview-report">
          <span className="dashboard-icon purple">
            <FileText size={22} />
          </span>
          <p className="dashboard-eyebrow">BRING THE WHOLE PICTURE TOGETHER</p>
          <h2>
            A plan you can share.
            <br />A direction everyone can follow.
          </h2>
          <p>
            Turn your company’s thinking into a clear visual report, ready for your next team
            conversation.
          </p>
          <Link to={route('visual-report')} className="dashboard-button dashboard-button-secondary">
            Build your visual report
            <ArrowRight size={17} />
          </Link>
        </section>
      </div>
      <Dialog.Root
        open={inviteOpen}
        onOpenChange={(open) => {
          if (!inviting) setInviteOpen(open);
        }}
      >
        <Dialog.Portal>
          <Dialog.Overlay className="company-invite-overlay" />
          <Dialog.Content
            className="company-invite-dialog"
            onEscapeKeyDown={(event) => {
              if (inviting) event.preventDefault();
            }}
            onInteractOutside={(event) => {
              if (inviting) event.preventDefault();
            }}
          >
            <Dialog.Title>Bring your people into the plan.</Dialog.Title>
            <Dialog.Description>
              Invite someone to {company.name} and choose how they can contribute.
            </Dialog.Description>
            <Dialog.Close
              className="company-invite-close"
              aria-label="Close invitation"
              disabled={inviting}
            >
              <X size={20} />
            </Dialog.Close>
            <form onSubmit={invite}>
              <label htmlFor="company-invite-email">Email address</label>
              <input
                id="company-invite-email"
                type="email"
                value={email}
                onChange={(event) => setEmail(event.target.value)}
                required
                placeholder="colleague@company.com"
                disabled={inviting}
              />
              <label htmlFor="company-invite-role">Company role</label>
              <select
                id="company-invite-role"
                value={role}
                onChange={(event) => setRole(event.target.value)}
                disabled={inviting}
              >
                <option value="viewer">Viewer — can view the plan</option>
                <option value="member">Member — can contribute to the plan</option>
                <option value="admin">Admin — can manage the company</option>
                <option value="owner">Owner — full company access</option>
              </select>
              {inviteResult && (
                <p
                  role={inviteResult.success ? 'status' : 'alert'}
                  className={`company-invite-result ${inviteResult.success ? 'success' : 'error'}`}
                >
                  {inviteResult.message}
                </p>
              )}
              <div className="company-overview-actions">
                <Dialog.Close
                  className="dashboard-button dashboard-button-secondary"
                  type="button"
                  disabled={inviting}
                >
                  Close
                </Dialog.Close>
                <button
                  className="dashboard-button dashboard-button-primary"
                  type="submit"
                  disabled={inviting || !email.trim()}
                >
                  {inviting ? (
                    <Loader2 size={16} className="animate-spin" />
                  ) : (
                    <UserPlus size={16} />
                  )}
                  {inviting ? 'Sending…' : 'Send invitation'}
                </button>
              </div>
            </form>
          </Dialog.Content>
        </Dialog.Portal>
      </Dialog.Root>
    </div>
  );
}
